import React, { useState, useEffect } from 'react';
import { BRAND, IS_RECMAPPER } from './product';
import ClaudeLearnTab from './components/ClaudeLearnTab';
import { UpdateBody, UpdateMedia, CategoryLabel } from './components/WhatsNewPanel';
import { useAuth } from './AuthContext';
import { EmbedRenderer } from './components/FeatureAnnouncementModal';
import { LESSONS, LESSON_FOLDERS, Lesson } from './tour/tours';
import { useTour } from './tour/TourProvider';
import { hasSearch } from './utils/searchAccess';

interface AnnouncementListItem {
  id: number;
  title: string;
  body: string | null;
  storylane_url: string | null;
  created_at: string;
  seen: boolean;
  dismissed_at: string | null;
  image_url?: string | null;
  video_url?: string | null;
  cta_label?: string | null;
  cta_url?: string | null;
  category?: string | null;
}

type Tab = 'training' | 'claude' | 'whats-new';

const Training: React.FC = () => {
  const { token } = useAuth();
  // /training?tab=claude opens Using Claude (MarketMapr only).
  const [activeTab, setActiveTab] = useState<Tab>(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    return t === 'claude' && !IS_RECMAPPER ? 'claude' : t === 'whats-new' ? 'whats-new' : 'training';
  });
  const [announcements, setAnnouncements] = useState<AnnouncementListItem[]>([]);
  const [loadingAnnouncements, setLoadingAnnouncements] = useState(false);
  const [activeAnnouncement, setActiveAnnouncement] = useState<AnnouncementListItem | null>(null);

  // Fetch announcements lazily — only when the user clicks the "What's New"
  // tab for the first time.
  useEffect(() => {
    if (activeTab !== 'whats-new' || announcements.length > 0 || loadingAnnouncements) return;
    fetchAnnouncements();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAnnouncements = async () => {
    setLoadingAnnouncements(true);
    try {
      const response = await fetch('/api/announcements/all-visible', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAnnouncements(data.announcements || []);
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
    } finally {
      setLoadingAnnouncements(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#334155] text-white py-12 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-4xl font-bold tracking-tight mb-3">Learn</h1>
          <p className="text-lg text-slate-300">
            Walkthroughs, guides, and the latest product updates from {BRAND.name}
          </p>
        </div>
      </div>

      {/* Sub-tab switcher */}
      <div className="border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6">
          <nav className="flex gap-6 -mb-px">
            <button
              onClick={() => setActiveTab('training')}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === 'training'
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Lessons
            </button>
            {!IS_RECMAPPER && (
              <button
                onClick={() => setActiveTab('claude')}
                className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                  activeTab === 'claude'
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Using Claude
              </button>
            )}
            <button
              onClick={() => setActiveTab('whats-new')}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors flex items-center gap-1.5 ${
                activeTab === 'whats-new'
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              What's New
              {announcements.some(a => !a.seen) && (
                <span className="inline-flex items-center justify-center w-1.5 h-1.5 rounded-full bg-blue-500" />
              )}
            </button>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-10">
        {activeTab === 'training' ? (
          <LessonsTab onOpenTab={(t) => { setActiveTab(t); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
        ) : activeTab === 'claude' ? (
          <ClaudeLearnTab />
        ) : (
          <WhatsNewTab
            loading={loadingAnnouncements}
            announcements={announcements}
            onSelect={setActiveAnnouncement}
          />
        )}
      </div>

      {activeAnnouncement && (
        <AnnouncementViewer
          announcement={activeAnnouncement}
          onClose={() => setActiveAnnouncement(null)}
        />
      )}
    </div>
  );
};

// ─── Lessons tab: guided lessons that run on the real screens ─────────
// Content comes from src/tour/tours.ts — the same steps drive the in-app
// tour and the written guide below, so the two can't drift apart.

const LessonsTab: React.FC<{ onOpenTab: (t: 'claude') => void }> = ({ onOpenTab }) => {
  const { user } = useAuth();
  const { startLesson, completed } = useTour();
  const [openGuide, setOpenGuide] = useState<string | null>(null);
  const canSearch = hasSearch(user as any);
  // Connect Claude needs the connector on this plan (Growth / Scale, or a
  // Starter month offered or running). null = still checking.
  const [canClaude, setCanClaude] = useState<boolean | null>(null);
  useEffect(() => {
    let off = false;
    fetch('/api/api-keys', { headers: { Authorization: `Bearer ${window.localStorage.getItem('token')}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!off) setCanClaude(!!d && (!!d.apiAllowed || !!d.connectorOffer)); })
      .catch(() => { if (!off) setCanClaude(false); });
    return () => { off = true; };
  }, []);
  const [openFolder, setOpenFolder] = useState<string | null>(null);
  // /training?lesson=<id> (links in the onboarding emails): open that
  // lesson's folder and steps, and bring it into view.
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('lesson');
    const lesson = id ? LESSONS.find((l) => l.id === id) : null;
    if (!lesson) return;
    if (lesson.opensTab && !IS_RECMAPPER) { onOpenTab(lesson.opensTab); return; }
    if (lesson.folder) setOpenFolder(lesson.folder);
    setOpenGuide(lesson.id);
    const t = setTimeout(() => {
      const el = document.getElementById(`lesson-${lesson.id}`);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.classList.add('ring-2', 'ring-teal-400'); }
    }, 300);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // One lesson card. `n` is its number within its list (top level or folder).
  const lessonCard = (lesson: Lesson, n: number) => {
          const done = completed.includes(lesson.id);
          const noClaude = !!lesson.needsConnector && canClaude !== true;
          const locked = (!!lesson.needsSearch && !canSearch) || noClaude;
          // Guides are read, not run; on Connect Claude, say when the plan doesn't include it.
          const guide = !!lesson.guideOnly;
          const claudeNote = guide && lesson.id === 'connect-claude' && canClaude === false;
          const guideOpen = openGuide === lesson.id;
          return (
            <div key={lesson.id} id={`lesson-${lesson.id}`} className="bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="p-5 flex gap-4 items-start flex-wrap sm:flex-nowrap">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 tabular-nums ${done ? 'bg-teal-600 text-white' : 'bg-teal-50 text-teal-700 border border-teal-200'}`}>
                  {done ? '✓' : n}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-gray-900">{lesson.title}</h3>
                    <span className="text-xs text-gray-400 tabular-nums">{lesson.opensTab ? `${lesson.minutes} min · examples and prompts` : `${lesson.minutes} min · ${lesson.steps.length} steps`}</span>
                    {done && <span className="text-[10px] font-semibold uppercase tracking-wide text-teal-700 bg-teal-50 rounded px-1.5 py-0.5">Completed</span>}
                  </div>
                  <p className="text-sm text-gray-500 mt-1 leading-relaxed">{lesson.summary}</p>
                  {claudeNote && (
                    <p className="text-xs text-amber-700 mt-2">Claude is part of the Growth and Scale plans. <a href="/settings?tab=subscription" className="underline">See the plans</a>.</p>
                  )}
                  {locked && (
                    <p className="text-xs text-amber-700 mt-2">
                      {!!lesson.needsSearch && !canSearch
                        ? 'This lesson uses Search, which isn’t switched on for your account yet.'
                        : canClaude === null ? 'Checking your plan…'
                          : <>Claude is part of the Growth and Scale plans. <a href="/settings?tab=subscription" className="underline">See the plans</a>; “Read the steps” shows how it works.</>}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {lesson.opensTab ? (
                    <button
                      onClick={() => onOpenTab(lesson.opensTab!)}
                      className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700"
                    >
                      Explore →
                    </button>
                  ) : guide ? (
                    <button
                      onClick={() => setOpenGuide(guideOpen ? null : lesson.id)}
                      className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700"
                      aria-expanded={guideOpen}
                    >
                      {guideOpen ? 'Hide guide' : 'Read the guide'}
                    </button>
                  ) : (<>
                  <button
                    onClick={() => setOpenGuide(guideOpen ? null : lesson.id)}
                    className="text-sm text-gray-500 hover:text-gray-800"
                    aria-expanded={guideOpen}
                  >
                    {guideOpen ? 'Hide steps' : 'Read the steps'}
                  </button>
                  <button
                    onClick={() => startLesson(lesson.id)}
                    disabled={locked}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {done ? 'Run again' : 'Start lesson'}
                  </button>
                  </>)}
                </div>
              </div>
              {guideOpen && (
                <ol className="border-t border-gray-100 px-5 py-4 space-y-3">
                  {lesson.steps.map((s, n) => (
                    <li key={s.id} className="flex gap-3">
                      <span className="text-xs font-semibold text-teal-700 tabular-nums w-5 flex-shrink-0 pt-0.5">{n + 1}.</span>
                      <div className="max-w-[65ch]">
                        <p className="text-sm font-medium text-gray-900">{s.title}</p>
                        <p className="text-sm text-gray-500 leading-relaxed">{s.body}</p>
                        {s.image && (
                          <img src={s.image} alt={s.title} loading="lazy"
                            className="mt-2 max-w-full max-h-80 object-contain rounded-lg border border-gray-200 shadow-sm" />
                        )}
                      </div>
                    </li>
                  ))}
                  {guide && lesson.id === 'connect-claude' && (
                    <li className="pl-8">
                      <a href="/settings?tab=integrations" className="text-sm font-medium text-teal-700 hover:underline">Open Settings → Integrations</a>
                      {!IS_RECMAPPER && (
                        <button onClick={() => onOpenTab('claude')} className="ml-4 text-sm font-medium text-teal-700 hover:underline">See examples and prompts to copy</button>
                      )}
                    </li>
                  )}
                </ol>
              )}
            </div>
          );
  };


  return (
    <div className="space-y-6">
      <div className="max-w-2xl">
        <h2 className="text-2xl font-bold text-gray-900" style={{ textWrap: 'balance' } as React.CSSProperties}>Learn by doing</h2>
        <p className="text-gray-500 mt-1.5 leading-relaxed">
          Each lesson runs on the real screens: we highlight what to click and move on when you’ve done it.
          Prefer to read? Every lesson has the steps written out too.
        </p>
      </div>

      <div className="space-y-4">
        {LESSONS.filter((l) => !l.folder && !(l.opensTab && IS_RECMAPPER)).map((lesson, i) => lessonCard(lesson, i + 1))}
      </div>

      {LESSON_FOLDERS.map((folder) => {
        const lessons = LESSONS.filter((l) => l.folder === folder.id);
        if (!lessons.length) return null;
        const doneN = lessons.filter((l) => completed.includes(l.id)).length;
        const open = openFolder === folder.id;
        return (
          <section key={folder.id} className="bg-gray-50 rounded-2xl border border-gray-200">
            <button
              className="w-full text-left p-5 flex items-start gap-4"
              onClick={() => setOpenFolder(open ? null : folder.id)}
              aria-expanded={open}
            >
              <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0" aria-hidden>
                <svg className="w-5 h-5 text-teal-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-gray-900">{folder.title}</h3>
                  <span className="text-xs text-gray-400 tabular-nums">{lessons.length} short lessons · {doneN}/{lessons.length} done</span>
                </div>
                <p className="text-sm text-gray-500 mt-1 leading-relaxed max-w-[65ch]">{folder.summary}</p>
              </div>
              <span className="text-sm text-gray-500 flex-shrink-0">{open ? 'Hide' : 'Show lessons'}</span>
            </button>
            {open && (
              <div className="px-5 pb-5 space-y-3">
                {lessons.map((lesson, i) => lessonCard(lesson, i + 1))}
              </div>
            )}
          </section>
        );
      })}

      <p className="text-sm text-gray-400">More lessons are on the way — monitoring, contact details and your team.</p>
    </div>
  );
};

// ─── What's New tab content ───────────────────────────────────────────

const WhatsNewTab: React.FC<{
  loading: boolean;
  announcements: AnnouncementListItem[];
  onSelect: (a: AnnouncementListItem) => void;
}> = ({ loading, announcements, onSelect }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (announcements.length === 0) {
    return (
      <div className="text-center py-20">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-700 mb-1">No announcements yet</h3>
        <p className="text-gray-500">Product updates will appear here as they're released.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 max-w-3xl mx-auto">
      {announcements.map(a => (a.image_url || a.video_url || a.cta_url) ? (
        // Updates written with the feature (What's new): shown in full.
        <article key={a.id} className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center gap-2">
            <CategoryLabel category={a.category} />
            <span className="text-xs text-gray-400">{new Date(a.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
          </div>
          <h3 className="mt-2 text-lg font-semibold text-gray-900 tracking-tight">{a.title}</h3>
          {a.body && <div className="mt-2 text-sm text-gray-600 leading-relaxed max-w-[65ch]"><UpdateBody text={a.body} /></div>}
          <div className="mt-4"><UpdateMedia u={{ ...a, seen: a.seen }} /></div>
          {a.cta_url && (
            <a href={a.cta_url} className="mt-4 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800">
              {a.cta_label || 'Try it'} <span aria-hidden>→</span>
            </a>
          )}
        </article>
      ) : (
        <button
          key={a.id}
          onClick={() => onSelect(a)}
          className="w-full bg-white rounded-xl border border-gray-200 hover:border-blue-200 hover:shadow-md transition-all duration-200 p-5 text-left flex items-start gap-4 group"
        >
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-sm">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-base font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">{a.title}</h3>
              {!a.seen && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-blue-100 text-blue-700 rounded-full">
                  New
                </span>
              )}
              {a.storylane_url && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-purple-100 text-purple-700 rounded-full">
                  Demo
                </span>
              )}
            </div>
            {a.body && (
              <p className="text-sm text-gray-600 line-clamp-2 mb-2 whitespace-pre-line">{a.body}</p>
            )}
            <p className="text-xs text-gray-400">
              {new Date(a.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>
        </button>
      ))}
    </div>
  );
};

// ─── Announcement viewer modal (re-watch) ─────────────────────────────

const AnnouncementViewer: React.FC<{
  announcement: AnnouncementListItem;
  onClose: () => void;
}> = ({ announcement, onClose }) => {
  return (
    <div className="modal-backdrop flex items-center justify-center" onClick={onClose}>
      <div
        className="modal-dialog max-w-2xl w-full mx-4 max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-200/80 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm flex-shrink-0">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 tracking-tight truncate">{announcement.title}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-2xl leading-none ml-3"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {announcement.body && (
            <div
              className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap mb-4"
              dangerouslySetInnerHTML={{
                __html: announcement.body.replace(
                  /(https?:\/\/[^\s<]+)/g,
                  '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">$1</a>'
                )
              }}
            />
          )}

          {announcement.storylane_url && (
            announcement.storylane_url.includes('<') ? (
              <EmbedRenderer key={announcement.id} embedCode={announcement.storylane_url} />
            ) : (
              <div className="mt-4 rounded-xl overflow-hidden border border-gray-200/80 shadow-card">
                <div className="sl-embed" style={{ position: 'relative', paddingBottom: 'calc(56.25% + 25px)', width: '100%', height: 0 }}>
                  <iframe
                    loading="lazy"
                    className="sl-demo"
                    src={announcement.storylane_url}
                    allow="fullscreen"
                    allowFullScreen
                    style={{
                      position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                      border: 'none', borderRadius: '10px'
                    }}
                  />
                </div>
              </div>
            )
          )}

          <p className="text-[10px] text-gray-400 mt-4">
            {new Date(announcement.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        <div className="px-6 py-3 border-t border-gray-200/80 flex justify-end">
          <button onClick={onClose} className="btn-primary">Close</button>
        </div>
      </div>
    </div>
  );
};

export default Training;
